import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  LayoutGrid,
  List,
  Pencil,
  Search,
  Trash2,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import { fetchEntity } from "../../../Apis/Entity";
import Layout4 from "../../DataLayouts/Layout4";

const ENTITY_TYPE_COLORS = {
  "head office": "#3f6fd6",
  branch: "#0d8f6f",
  virtual: "#7a59c2",
};

function entityInitials(short, name) {
  const src = String(short || name || "").trim();
  if (!src) return "EN";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }
  return src.slice(0, 2).toUpperCase();
}

function pgList(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, "…", pages];
  if (page >= pages - 3) {
    return [1, "…", pages - 4, pages - 3, pages - 2, pages - 1, pages];
  }
  return [1, "…", page - 1, page, page + 1, "…", pages];
}

function getTypeColor(typeName) {
  const key = String(typeName || "").trim().toLowerCase();
  return ENTITY_TYPE_COLORS[key] || "var(--ems-muted)";
}

function getTypeName(item) {
  return (
    item?.entity_type?.organization_entity_type_name ||
    item?.EntityType?.organization_entity_type_name ||
    "—"
  );
}

function getShortName(item) {
  return item?.entity_code || item?.entity_short_name || "—";
}

function formatLocation(item) {
  const loc = item?.location || item?.Location;
  if (!loc) return "—";
  const city = loc?.city?.city_name || loc?.city_name || "";
  const state = loc?.state?.state_name || loc?.state_name || "";
  if (city && state) return `${city}, ${state}`;
  return loc?.location_name || "—";
}

function getGstin(item) {
  const val = item?.gstin || item?.gst_number || item?.registration?.gstin;
  return val ? String(val) : "—";
}

function EntityTypeTag({ typeName }) {
  const color = getTypeColor(typeName);
  return (
    <span
      className="ems-entity-type"
      style={{
        color,
        borderColor: `color-mix(in srgb, ${color} 40%, var(--ems-surface))`,
        background: `color-mix(in srgb, ${color} 12%, var(--ems-surface))`,
      }}
    >
      {typeName}
    </span>
  );
}

function OrganizationEntityList() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rpp, setRpp] = useState(25);
  const [view, setView] = useState("list");
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const org = userData?.organization;
  const orgId = org?.organization_id;

  const canEdit = Permission?.includes("ENTITY_EDIT");
  const canDelete = Permission?.includes("ENTITY_DELETE");
  const canView =
    canEdit || Permission?.includes("ENTITY_ALL") || Permission?.includes("ENTITY_VIEW");

  const loadEntities = React.useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await fetchEntity(orgId);
      const rows = data?.entity || [];
      setEntities(
        rows.map((item) => ({
          ...item,
          id: item.organization_entity_id,
        }))
      );
    } catch {
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  const filtered = useMemo(() => {
    const q = String(searchTerm || "").trim().toLowerCase();
    if (!q) return entities;
    return entities.filter((e) => {
      const hay = [
        e.entity_name,
        getShortName(e),
        getTypeName(e),
        formatLocation(e),
        getGstin(e),
      ]
        .filter((v) => v && v !== "—")
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [entities, searchTerm]);

  const pages = Math.max(1, Math.ceil(filtered.length / rpp));
  const curPage = Math.min(page, pages);
  const slice = filtered.slice((curPage - 1) * rpp, curPage * rpp);

  const deleteEntity = async (id) => {
    if (!canDelete || !orgId) return;
    if (!window.confirm("Delete this organization entity? This cannot be undone.")) {
      return;
    }
    try {
      const response = await axios.delete(
        `${MAIN_URL}/api/organizations/${orgId}/entity/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(response.data?.message || "Entity deleted successfully.");
      setEntities((prev) => prev.filter((e) => e.organization_entity_id !== id));
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
        return;
      }
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to delete entity"
      );
    }
  };

  const renderPager = () => (
    <div className="ems-pager">
      <span className="ems-pg-info">
        Showing <b>{slice.length}</b> of <b>{filtered.length}</b> entities
      </span>
      <div className="ems-pg-spacer" />
      <div className="ems-rpp">
        Rows per page
        <select
          className="ems-select"
          value={rpp}
          onChange={(e) => {
            setRpp(Number(e.target.value));
            setPage(1);
          }}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      <span className="ems-pg-page">
        Page <b>{curPage}</b> of <b>{pages}</b>
      </span>
      <div className="ems-pg-nav">
        <button
          type="button"
          className="ems-pg-btn"
          disabled={curPage <= 1}
          onClick={() => setPage(1)}
          aria-label="First page"
        >
          <ChevronsLeft size={16} />
        </button>
        <button
          type="button"
          className="ems-pg-btn"
          disabled={curPage <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>
        {pgList(curPage, pages).map((n, i) =>
          n === "…" ? (
            <span key={`e-${i}`} className="ems-pg-ellipsis">
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              className={`ems-pg-num${n === curPage ? " active" : ""}`}
              onClick={() => setPage(n)}
            >
              {n}
            </button>
          )
        )}
        <button
          type="button"
          className="ems-pg-btn"
          disabled={curPage >= pages}
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          className="ems-pg-btn"
          disabled={curPage >= pages}
          onClick={() => setPage(pages)}
          aria-label="Last page"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="ems-dashboard ems-fade-up">
      <Layout4
        heading="Organization Entities"
        subtitle={`Organization Setup • ${entities.length} registered entit${entities.length === 1 ? "y" : "ies"}`}
        Route="/organization/entity"
        btnName="Add Entity"
        add_action="ENTITY_ADD"
      />
      <div className="ems-section-divider" />

      <div className="ems-card ems-dt-toolbar">
        <div className="ems-dt-search">
          <Search size={16} />
          <input
            type="search"
            placeholder="Search entities..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="ems-dt-tools">
          <div className="ems-seg icons">
            <button
              type="button"
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
              aria-label="List view"
            >
              <List size={16} />
            </button>
            <button
              type="button"
              className={view === "grid" ? "active" : ""}
              onClick={() => setView("grid")}
              aria-label="Grid view"
            >
              <LayoutGrid size={16} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="ems-loading-screen">Loading…</div>
      ) : !filtered.length ? (
        <div className="ems-card ems-col-empty" style={{ padding: 56, textAlign: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: "var(--ems-text)", marginBottom: 8 }}>
            No results found
          </div>
          <div style={{ color: "var(--ems-muted)", fontSize: 14, marginBottom: 20 }}>
            Try adjusting your search, or add a new organization entity to get started.
          </div>
          {Permission?.includes("ENTITY_ADD") ? (
            <button
              type="button"
              className="ems-btn ems-primary"
              onClick={() => navigate("/organization/entity/add")}
            >
              Add Entity
            </button>
          ) : null}
        </div>
      ) : view === "grid" ? (
        <>
          <div className="ems-dt-grid">
            {slice.map((e) => {
              const typeName = getTypeName(e);
              const short = getShortName(e);
              return (
                <div className="ems-dt-card" key={e.organization_entity_id}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div className="ems-act-cell">
                      <span className="ems-org-mark">
                        {entityInitials(short, e.entity_name)}
                      </span>
                      <div>
                        <div className="ems-lead-name">{e.entity_name}</div>
                        <div className="ems-org-type">{formatLocation(e)}</div>
                      </div>
                    </div>
                    {typeName !== "—" ? <EntityTypeTag typeName={typeName} /> : null}
                  </div>
                  <div style={{ marginTop: 12, fontSize: 13 }}>
                    <div>
                      <span className="ems-org-type">Short: </span>
                      <span className="ems-ls-short">{short}</span>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <span className="ems-org-type">GSTIN: </span>
                      <span className="ems-tnum" style={{ color: "var(--ems-muted)", fontSize: 12.5 }}>
                        {getGstin(e)}
                      </span>
                    </div>
                  </div>
                  <div className="ems-row-actions" style={{ marginTop: 14 }}>
                    {canView ? (
                      <button
                        type="button"
                        className="ems-icon-link"
                        title="View"
                        onClick={() =>
                          navigate(`/organization/entity/view/${e.organization_entity_id}`)
                        }
                      >
                        <Eye size={15} />
                      </button>
                    ) : null}
                    {canEdit ? (
                      <button
                        type="button"
                        className="ems-icon-link"
                        title="Edit"
                        onClick={() =>
                          navigate(`/organization/entity/edit/${e.organization_entity_id}`)
                        }
                      >
                        <Pencil size={15} />
                      </button>
                    ) : null}
                    {canDelete ? (
                      <button
                        type="button"
                        className="ems-icon-link danger"
                        title="Delete"
                        onClick={() => deleteEntity(e.organization_entity_id)}
                      >
                        <Trash2 size={15} />
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="ems-card" style={{ marginTop: 14 }}>
            {renderPager()}
          </div>
        </>
      ) : (
        <div className="ems-card ems-table-card">
          <div className="ems-table-wrap">
            <table className="ems-data ems-entity-table">
              <thead>
                <tr>
                  <th>Entity Name</th>
                  <th>Short Name</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>GSTIN</th>
                  <th className="ems-num" style={{ width: 128 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {slice.map((e) => {
                  const typeName = getTypeName(e);
                  const short = getShortName(e);
                  const gstin = getGstin(e);
                  return (
                    <tr key={e.organization_entity_id}>
                      <td>
                        <div className="ems-act-cell">
                          <span className="ems-org-mark">
                            {entityInitials(short, e.entity_name)}
                          </span>
                          <span className="ems-lead-name">{e.entity_name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="ems-ls-short">{short}</span>
                      </td>
                      <td>
                        {typeName !== "—" ? (
                          <EntityTypeTag typeName={typeName} />
                        ) : (
                          <span className="ems-rep-zero">—</span>
                        )}
                      </td>
                      <td>
                        <span className="ems-org-type">{formatLocation(e)}</span>
                      </td>
                      <td>
                        <span
                          className={`ems-tnum${gstin === "—" ? " ems-rep-zero" : ""}`}
                          style={{ fontSize: 12.5 }}
                        >
                          {gstin}
                        </span>
                      </td>
                      <td className="ems-num">
                        <div className="ems-row-actions" style={{ justifyContent: "flex-end" }}>
                          {canView ? (
                            <button
                              type="button"
                              className="ems-icon-link"
                              title="View"
                              onClick={() =>
                                navigate(
                                  `/organization/entity/view/${e.organization_entity_id}`
                                )
                              }
                            >
                              <Eye size={15} />
                            </button>
                          ) : null}
                          {canEdit ? (
                            <button
                              type="button"
                              className="ems-icon-link"
                              title="Edit"
                              onClick={() =>
                                navigate(
                                  `/organization/entity/edit/${e.organization_entity_id}`
                                )
                              }
                            >
                              <Pencil size={15} />
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              className="ems-icon-link danger"
                              title="Delete"
                              onClick={() => deleteEntity(e.organization_entity_id)}
                            >
                              <Trash2 size={15} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {renderPager()}
        </div>
      )}
    </div>
  );
}

export default OrganizationEntityList;
